<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import dayjs from '@/lib/dayjs'
const SORT_OPTIONS = [
  { value: '-start_time', text: 'По дате: сначала новые' },
  { value: 'start_time', text: 'По дате: сначала старые' },
  { value: '-price', text: 'По цене: сначала дорогие' },
  { value: 'price', text: 'По цене: сначала дешёвые' }
]

const LIST_MODE_OPTIONS = [
  { value: 'upcoming', text: 'Афиша' },
  { value: 'finished', text: 'Прошедшие' },
  { value: 'mine', text: 'Мои вебинары' },
  { value: 'liked', text: 'Избранное' }
]

const AUDIENCE_OPTIONS = [
  { value: 'p', text: 'Родители' },
  { value: 't', text: 'Учителя' }
]

type Filters = Record<string, unknown>

const props = withDefaults(defineProps<{ filters?: Filters }>(), { filters: () => ({}) })

const emit = defineEmits<{ (e: 'update:filters', value: Filters): void }>()

const filtersModalOpen = ref(false)
const sortOptions = SORT_OPTIONS
const listModeOptions = LIST_MODE_OPTIONS
const audienceOptions = AUDIENCE_OPTIONS
const draft = reactive({
  list_mode: null as string | null,
  min_price: '' as string | number,
  max_price: '' as string | number,
  date_range: null as { start: unknown; end: unknown } | null,
  access_policy: [] as string[],
  has_video: null as boolean | null
})

const activeListMode = computed(() => {
  const current = props.filters || {}
  if (current.liked) return 'liked'
  if (current.purchased_or_organized) return 'mine'
  if (current.time_to && !current.time_from) return 'finished'
  if (current.time_from && !current.time_to) return 'upcoming'
  return null
})

const activeSort = computed(() => String(props.filters?.order || 'start_time'))

function openFilters() {
  syncDraft()
  filtersModalOpen.value = true
}

function syncDraft() {
  const current = props.filters || {}
  const accessPolicy =
    typeof current.access_policy === 'string'
      ? current.access_policy
        .split(',')
        .map(value => value.trim())
        .filter(Boolean)
      : []

  const dateRange =
    current.date_from && current.date_to
      ? { start: dayjs(String(current.date_from)), end: dayjs(String(current.date_to)) }
      : null

  draft.list_mode = activeListMode.value
  draft.min_price = (current.min_price ?? '') as string | number
  draft.max_price = (current.max_price ?? '') as string | number
  draft.date_range = dateRange
  draft.access_policy = accessPolicy
  draft.has_video = current.has_video === true ? true : null
}

function applyFilters() {
  const next = { ...(props.filters || {}) }
  const listMode = draft.list_mode
  applyListMode(next, listMode)

  const minPrice = toNumber(draft.min_price)
  const maxPrice = toNumber(draft.max_price)
  applyFields(next, { min_price: minPrice, max_price: maxPrice })

  const hasDateRange = draft.date_range?.start && draft.date_range?.end
  if (listMode === 'upcoming' || listMode === 'finished' || !hasDateRange) {
    applyFields(next, { date_from: null, date_to: null })
  } else {
    const start = dayjs(draft.date_range!.start as string).format('YYYY-MM-DD')
    const end = dayjs(draft.date_range!.end as string).format('YYYY-MM-DD')
    applyFields(next, { date_from: start, date_to: end })
  }

  applyFields(next, { access_policy: draft.access_policy.join(',') || null })
  applyFields(next, { has_video: draft.has_video || null })

  emit('update:filters', next)
  filtersModalOpen.value = false
}

function clearFilters() {
  const next: Filters = {}
  if (props.filters?.order) next.order = props.filters.order
  emit('update:filters', next)
  filtersModalOpen.value = false
}

function setListMode(mode: string) {
  const next = { ...(props.filters || {}) }
  const nextMode = activeListMode.value === mode ? null : mode
  applyListMode(next, nextMode)
  emit('update:filters', next)
}

function setSort(order: string) {
  const next = { ...(props.filters || {}) }
  next.order = order
  emit('update:filters', next)
}

function applyListMode(target: Filters, mode: string | null) {
  applyFields(target, { liked: null, purchased_or_organized: null, time_from: null, time_to: null })

  if (mode === 'upcoming') {
    applyFields(target, { time_from: Math.floor(Date.now() / 1000), date_from: null, date_to: null })
    return
  }
  if (mode === 'finished') {
    applyFields(target, { time_to: Math.floor(Date.now() / 1000), date_from: null, date_to: null })
    return
  }
  if (mode === 'mine') {
    applyFields(target, { purchased_or_organized: true })
    return
  }
  if (mode === 'liked') {
    applyFields(target, { liked: true })
  }
}

function toggleAudience(value: string) {
  const current = Array.isArray(draft.access_policy) ? [...draft.access_policy] : []
  const idx = current.indexOf(value)
  if (idx === -1) current.push(value)
  else current.splice(idx, 1)
  draft.access_policy = current
}

function applyFields(obj: Filters, values: Filters) {
  Object.entries(values || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') delete obj[key]
    else obj[key] = value
  })
}

function toNumber(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}
</script>

<template>
  <ul class="webinar-mobile-list-toolbar mb-2">
    <li class="webinar-mobile-list-toolbar__icon">
      <v-link @click="openFilters">
        <v-icon size="28">mdi-filter</v-icon>
      </v-link>
    </li>

    <li class="webinar-mobile-list-toolbar__icon">
      <v-menu-base pos-horizontal="center" pos-vertical="bottom">
        <template #activator="{ onclick }">
          <v-link @click="onclick">
            <v-icon size="28">mdi-swap-vertical</v-icon>
          </v-link>
        </template>

        <div class="webinar-mobile-list-toolbar__menu">
          <v-menu-button v-for="option in sortOptions" :key="option.value" @click="() => setSort(option.value)">
            <span class="flex-spacer">{{ option.text }}</span>
            <v-icon v-if="activeSort === option.value" class="green-text">mdi-check</v-icon>
          </v-menu-button>
        </div>
      </v-menu-base>
    </li>

    <li
      v-for="option in listModeOptions"
      :key="option.value"
      class="webinar-mobile-list-toolbar__chip"
      :class="{ 'webinar-mobile-list-toolbar__chip_active': activeListMode === option.value }"
    >
      <v-link @click="() => setListMode(option.value)">
        {{ option.text }}
      </v-link>
      <button
        v-if="activeListMode === option.value"
        class="webinar-mobile-list-toolbar__chip-close"
        type="button"
        @click.stop="() => setListMode(option.value)"
      >
        <v-icon size="18">mdi-close</v-icon>
      </button>
    </li>

    <v-rich-modal v-model="filtersModalOpen" title="Фильтры">
      <v-field>
        <div class="webinar-mobile-list-toolbar__chips">
          <button
            v-for="option in listModeOptions"
            :key="option.value"
            class="webinar-mobile-list-toolbar__chip-toggle"
            :class="{ 'webinar-mobile-list-toolbar__chip-toggle_active': draft.list_mode === option.value }"
            type="button"
            @click="
              () => {
                draft.list_mode = draft.list_mode === option.value ? null : option.value
              }
            "
          >
            <span>{{ option.text }}</span>
            <v-icon v-if="draft.list_mode === option.value" class="webinar-mobile-list-toolbar__chip-icon" size="16">
              mdi-close
            </v-icon>
          </button>
        </div>
      </v-field>

      <v-field class="mt-3">
        <template #label>
          <p class="text-body-bold">Стоимость участия</p>
        </template>
        <div class="d-flex">
          <v-input v-model="draft.min_price" placeholder="0" type="number">
            <template #prepend>от&nbsp;</template>
          </v-input>

          <v-input v-model="draft.max_price" class="ml-2" placeholder="100 000" type="number">
            <template #prepend>до&nbsp;</template>
          </v-input>
        </div>
      </v-field>

      <v-field class="mt-3">
        <template #label>
          <p class="text-body-bold">Дата проведения</p>
        </template>
        <v-date-range-picker v-model="draft.date_range" />
      </v-field>

      <v-field class="mt-3">
        <template #label>
          <p class="text-body-bold">Приглашенные участники</p>
        </template>
        <div class="webinar-mobile-list-toolbar__chips">
          <button
            v-for="audience in audienceOptions"
            :key="audience.value"
            class="webinar-mobile-list-toolbar__chip-toggle"
            :class="{ 'webinar-mobile-list-toolbar__chip-toggle_active': draft.access_policy.includes(audience.value) }"
            type="button"
            @click="() => toggleAudience(audience.value)"
          >
            {{ audience.text }}
            <v-icon
              v-if="draft.access_policy.includes(audience.value)"
              class="webinar-mobile-list-toolbar__chip-icon"
              size="16"
            >
              mdi-close
            </v-icon>
          </button>
        </div>
      </v-field>

      <v-field class="mt-3">
        <template #label>
          <p class="text-body-bold">Запись</p>
        </template>
        <v-checkbox
          :model-value="draft.has_video === true"
          @update:model-value="
            (val: boolean) => {
              draft.has_video = val ? true : null
            }
          "
        >
          Есть видеозапись
        </v-checkbox>
      </v-field>

      <template #actions>
        <v-button mode="large primary" @click="applyFilters"> Применить </v-button>
        <v-button mode="large text" @click="clearFilters"> Сбросить </v-button>
      </template>
    </v-rich-modal>
  </ul>
</template>

<style lang="scss">
.webinar-mobile-list-toolbar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  li {
    background: $lightenGray;
    color: $blue;
    border-radius: 999px;
    padding: 0 12px;

    height: 40px;
    display: flex;
    align-items: center;
  }

  &__icon {
    padding: 0 8px;
  }

  &__chip_active {
    background: $lightenBlue;
    color: $blue;

    .v-link {
      color: $blue;
    }
  }

  &__chip-close {
    margin-left: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: inherit;
  }

  &__menu {
    min-width: 240px;
  }

  &__chips {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__chip-toggle {
    border: 1px solid $lightGray;
    background: white;
    color: $darkGray;
    border-radius: 999px;
    padding: 6px 12px;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__chip-toggle_active {
    background: $blue;
    color: white;
    border-color: $blue;
  }

  &__chip-icon {
    color: inherit;
  }
}
</style>
